import {
  normalizeReplayBundle, buildReplayIndex,
  buildAudioTimeline, splitEventPages,
} from './replayEngine.js';

const MAX_EVENT_PAGES = 400; // 400 * 500 = 200k events
const REPLAY_EVENT_PAGE_SIZE = 500;

export class ReplayNetworkError extends Error {
  constructor(code, status) {
    super(code);
    this.code = code;
    this.status = status;
  }
}

const requireId = (value, name) => {
  if (typeof value === 'number' && Number.isSafeInteger(value) && value > 0) return String(value);
  if (typeof value === 'string' && /^[a-zA-Z0-9_-]{1,128}$/.test(value)) return value;
  throw new ReplayNetworkError('INVALID_' + name);
};

const checkResponse = async (response) => {
  if (response.ok) return response;
  let code = 'REPLAY_REQUEST_FAILED';
  try {
    const parsed = await response.json();
    if (typeof parsed?.code === 'string') code = parsed.code;
  } catch {}
  throw new ReplayNetworkError(code, response.status);
};

export const createReplayTransport = ({
  gameId, recordingId, token, shareToken, libraryScope = false, apiUrl, fetchImpl = fetch,
}) => {
  const shared = shareToken !== undefined && shareToken !== null;
  if (typeof apiUrl !== 'function') throw new ReplayNetworkError('REPLAY_INVALID_API');
  if (shared && (typeof shareToken !== 'string' || !/^[A-Za-z0-9_-]{43}$/.test(shareToken))) {
    throw new ReplayNetworkError('SHARE_NOT_FOUND', 404);
  }
  if (!shared && (!token || typeof token !== 'string')) {
    throw new ReplayNetworkError('REPLAY_AUTH_REQUIRED', 401);
  }
  const base = shared ? apiUrl('/api/recording-shares')
    : libraryScope ? apiUrl('/api/recording-library/archives/' +
      requireId(recordingId, 'RECORDING_ID'))
      : apiUrl('/api/games/' + requireId(gameId, 'GAME_ID') +
        '/recordings/' + requireId(recordingId, 'RECORDING_ID'));
  const headers = shared ? { 'X-Recording-Share': shareToken } : {
    Authorization: 'Bearer ' + token,
  };
  const request = async (path, signal) => checkResponse(await fetchImpl(base + path, {
    method: 'GET', signal, cache: 'no-store', referrerPolicy: 'no-referrer',
    headers,
  }));

  return {
    async metadata(signal) {
      return (await request('', signal)).json();
    },
    async page(afterSequence, signal) {
      return (await request('/events?afterSequence=' + afterSequence
        + '&limit=' + REPLAY_EVENT_PAGE_SIZE, signal)).json();
    },
    async audioManifest(signal) {
      return (await request('/audio', signal)).json();
    },
    async visualAsset(key, signal) {
      if (typeof key !== 'string'
        || !/^(?:board|card-background|card-[1-9][0-9]?|chip-[a-zA-Z0-9_-]{1,40})$/.test(key)) {
        throw new ReplayNetworkError('INVALID_VISUAL_ASSET');
      }
      const response = await request('/assets/' + encodeURIComponent(key), signal);
      return response.blob();
    },
    async audioBlob(segmentIndex, signal) {
      if (!Number.isSafeInteger(segmentIndex) || segmentIndex < 0) {
        throw new ReplayNetworkError('INVALID_AUDIO_SEGMENT');
      }
      const response = await request('/audio/' + segmentIndex, signal);
      return response.blob();
    },
  };
};

export const loadReplayArchive = async ({
  gameId, recordingId, token, shareToken, libraryScope = false, apiUrl,
  signal, onPage, checkpointInterval = 150,
  fetchImpl = fetch,
}) => {
  const transport = createReplayTransport({
    gameId, recordingId, token, shareToken, libraryScope, apiUrl, fetchImpl,
  });
  const [metadata, audio] = await Promise.all([
    transport.metadata(signal), transport.audioManifest(signal),
  ]);
  const pages = [];
  let afterSequence = 0;
  let hasMore = true;
  let totalEvents = 0;
  for (let i = 0; hasMore; i++) {
    if (i >= MAX_EVENT_PAGES) throw new ReplayNetworkError('REPLAY_EVENT_LIMIT');
    const page = await transport.page(afterSequence, signal);
    if (!Array.isArray(page?.events) || typeof page.hasMore !== 'boolean'
      || !Number.isSafeInteger(page.nextSequence) ||
      page.nextSequence < afterSequence ||
      (page.hasMore && page.nextSequence === afterSequence)) {
      throw new ReplayNetworkError('REPLAY_INVALID_EVENT_PAGE');
    }
    pages.push(page);
    totalEvents += page.events.length;
    afterSequence = page.nextSequence;
    hasMore = page.hasMore;
    onPage?.({ totalEvents, hasMore, pageIndex: i + 1 });
  }

  const bundle = normalizeReplayBundle({
    recording: metadata.recording,
    initialSnapshot: metadata.initialSnapshot,
    assetsSnapshot: metadata.assetsSnapshot,
    events: splitEventPages(pages),
  });
  return {
    bundle,
    index: buildReplayIndex(bundle, checkpointInterval),
    audioTimeline: buildAudioTimeline(audio.segments),
    transport,
  };
};
